// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one live terminal in the Agents Terminals panel: a real terminal emulator (xterm.js) in
// Konvo's colours, fed by the machine's output and typed into from the keyboard (paste, copy and
// Ctrl-C as in any terminal; Ctrl-C with text selected copies it). On phones a key row above the
// keyboard gives Ctrl, Ctrl-C, Tab, Esc, the arrows and Return (design/agents-terminals p-a4).
//
// Size: a terminal opened from Konvo takes the panel's size and follows it. One opened in T3 keeps
// T3's width (Konvo never resizes it): it shows at that width and scrolls sideways when the panel is
// narrower; its height is the panel's.
//
// Safety: the emulator never writes the clipboard for the terminal (OSC 52) and never opens links
// (OSC 8 and plain URLs do nothing); the machine drops those sequences too.

import styles from '@app/features/konvo_agents/components/AgentTerminalScreen.module.css';
import AgentTerminalSessions from '@app/features/konvo_agents/state/AgentTerminalSessions';
import AgentTerminalViews, {
	bufferText,
	type TerminalScreen,
	type TerminalView,
	themeFrom,
} from '@app/features/konvo_agents/state/AgentTerminalViews';
import {ctrlKey, KEY_SEQUENCES} from '@app/features/konvo_agents/utils/AgentTerminalLink';
import type {AgentLiveTerminal} from '@konvo/schema/src/domains/konvo/AgentTerminals';
import {useLingui} from '@lingui/react/macro';
import {IconArrowDown, IconArrowLeft, IconArrowRight, IconArrowUp, IconCornerDownLeft} from '@tabler/icons-react';
import '@xterm/xterm/css/xterm.css';
import {clsx} from 'clsx';
import {useEffect, useRef, useState} from 'react';

export interface TerminalScreenApi {
	/** The selection, else everything on screen and in the scrollback. */
	text(): string;
	focus(): void;
}

export function AgentTerminalScreen({
	machineId,
	threadId,
	terminal,
	phone,
	disabled,
	onReady,
}: {
	machineId: string;
	threadId: string;
	terminal: AgentLiveTerminal;
	phone: boolean;
	/** Offline or ended: the screen stays, typing stops. */
	disabled: boolean;
	onReady?: (api: TerminalScreenApi | null) => void;
}) {
	const {t} = useLingui();
	const frame = useRef<HTMLDivElement>(null);
	const viewRef = useRef<TerminalView | null>(null);
	const screenRef = useRef<TerminalScreen | null>(null);
	const [ctrl, setCtrl] = useState(false);
	const ctrlRef = useRef(false);
	ctrlRef.current = ctrl;
	const id = terminal.id;
	const own = terminal.own;

	useEffect(() => {
		const outer = frame.current;
		if (!outer) return;
		const screen: TerminalScreen = {
			frame: outer,
			hostClass: styles.host,
			phone,
			disabled,
			typed: (input) => {
				if (!ctrlRef.current) return input;
				setCtrl(false);
				return ctrlKey(input) ?? input;
			},
		};
		screenRef.current = screen;
		// The emulator kept for this terminal (with all its output), else a new one.
		const view = AgentTerminalViews.show({machineId, threadId, id, own}, screen);
		viewRef.current = view;
		const term = view.term;
		let disposed = false;
		// Once the mono font is in, measure the cells with it.
		void document.fonts?.ready.then(() => {
			if (disposed) return;
			view.layout();
		});
		let timer: ReturnType<typeof setTimeout> | null = null;
		const observer = new ResizeObserver(() => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(view.layout, 80);
		});
		observer.observe(outer);
		// The theme follows Konvo's (light, dark, glass).
		const themeWatch = new MutationObserver(() => {
			term.options.theme = themeFrom(outer);
		});
		themeWatch.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
		onReady?.({text: () => (term.hasSelection() ? term.getSelection() : bufferText(term)), focus: () => term.focus()});
		return () => {
			disposed = true;
			onReady?.(null);
			if (timer) clearTimeout(timer);
			observer.disconnect();
			themeWatch.disconnect();
			// Off screen, still running: coming back shows it as it is then.
			AgentTerminalViews.hide(view, screen);
			viewRef.current = null;
			screenRef.current = null;
		};
		// onReady is the panel's and `disabled` is followed below; either would rebuild this needlessly.
	}, [machineId, threadId, id, own, phone]);

	useEffect(() => {
		if (screenRef.current) screenRef.current.disabled = disabled;
		if (viewRef.current) viewRef.current.term.options.disableStdin = disabled;
	}, [disabled]);

	const send = (data: string) => {
		if (disabled) return;
		AgentTerminalSessions.input(machineId, threadId, id, data);
		viewRef.current?.term.focus();
	};
	const key = (label: string, data: string, flx: string, icon?: React.ReactNode) => (
		<button
			type="button"
			className={styles.key}
			// Keep the keyboard up: the button never takes the focus.
			onPointerDown={(event) => event.preventDefault()}
			onClick={() => send(data)}
			aria-label={label}
			disabled={disabled}
			data-flx={`konvo-agents.terminals.key.${flx}`}
		>
			{icon ?? label}
		</button>
	);

	return (
		<div className={styles.wrap}>
			<div
				ref={frame}
				className={clsx(styles.frame, !own && styles.frameScroll, disabled && styles.frameOff)}
				data-flx="konvo-agents.terminals.screen"
				data-id={id}
				data-own={own ? 'true' : 'false'}
			></div>
			{phone && (
				<div className={styles.keys} role="toolbar" data-flx="konvo-agents.terminals.keys">
					<button
						type="button"
						className={clsx(styles.key, ctrl && styles.keyOn)}
						onPointerDown={(event) => event.preventDefault()}
						onClick={() => {
							setCtrl((on) => !on);
							viewRef.current?.term.focus();
						}}
						aria-pressed={ctrl}
						disabled={disabled}
						data-flx="konvo-agents.terminals.key.ctrl"
					>
						{t({
							message: 'Ctrl',
							comment: 'Agents Terminals (phone): the Ctrl key above the keyboard; the next key is sent with Ctrl.',
						})}
					</button>
					{key(
						t({message: 'Ctrl C', comment: 'Agents Terminals (phone): key that sends Ctrl-C (stops what runs).'}),
						KEY_SEQUENCES.ctrlC,
						'ctrl-c',
					)}
					{key(
						t({message: 'Tab', comment: 'Agents Terminals (phone): the Tab key above the keyboard.'}),
						KEY_SEQUENCES.tab,
						'tab',
					)}
					{key(
						t({message: 'Esc', comment: 'Agents Terminals (phone): the Escape key above the keyboard.'}),
						KEY_SEQUENCES.esc,
						'esc',
					)}
					{key(
						t({message: 'Left', comment: 'Agents Terminals (phone): arrow key, read out by screen readers.'}),
						KEY_SEQUENCES.left,
						'left',
						<IconArrowLeft size={17} stroke={1.9} />,
					)}
					{key(
						t({message: 'Up', comment: 'Agents Terminals (phone): arrow key, read out by screen readers.'}),
						KEY_SEQUENCES.up,
						'up',
						<IconArrowUp size={17} stroke={1.9} />,
					)}
					{key(
						t({message: 'Down', comment: 'Agents Terminals (phone): arrow key, read out by screen readers.'}),
						KEY_SEQUENCES.down,
						'down',
						<IconArrowDown size={17} stroke={1.9} />,
					)}
					{key(
						t({message: 'Right', comment: 'Agents Terminals (phone): arrow key, read out by screen readers.'}),
						KEY_SEQUENCES.right,
						'right',
						<IconArrowRight size={17} stroke={1.9} />,
					)}
					{key(
						t({message: 'Return', comment: 'Agents Terminals (phone): the Return key, read out by screen readers.'}),
						KEY_SEQUENCES.enter,
						'enter',
						<IconCornerDownLeft size={17} stroke={1.9} />,
					)}
				</div>
			)}
		</div>
	);
}
